import { useCallback, useEffect, useState } from "react";
import { FlaskConical, Loader2, Plus, Trash2, Copy, Play, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ASSUMPTION_LABELS = {
  DIRECT_VALUE: "Valore diretto", PERCENT_CHANGE: "Variazione %",
  TARGET_VALUE: "Valore target", BOOLEAN_CHANGE: "Cambio sì/no",
};
const DIM_LABELS = { E: "Environmental", S: "Social", G: "Governance" };

function DeltaValue({ value, testid }) {
  if (value === null || value === undefined) return <span data-testid={testid}>—</span>;
  const cls = value > 0 ? "text-green-700" : value < 0 ? "text-red-600" : "text-slate-400";
  return <span className={`font-bold ${cls}`} data-testid={testid}>{value > 0 ? "+" : ""}{value}</span>;
}

export default function ScenariosSection({ companyId, period }) {
  const [scenarios, setScenarios] = useState(null);
  const [label, setLabel] = useState("");
  const [options, setOptions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [rows, setRows] = useState([{ code: "", type: "PERCENT_CHANGE", value: "" }]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/scenarios`);
      setScenarios(data.items);
      setLabel(data.label);
    } catch {
      setScenarios([]);
    }
    api.get(`/companies/${companyId}/periods/${period.id}/score/simulate/options`)
      .then((r) => setOptions(r.data.options || [])).catch(() => setOptions([]));
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  const setRow = (i, patch) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const create = async () => {
    const inputs = rows.filter((r) => r.code && r.value !== "").map((r) => ({
      canonical_datapoint_code: r.code,
      assumption_type: r.type,
      simulated_value: r.type === "BOOLEAN_CHANGE"
        ? r.value === "true"
        : Number(r.value),
    }));
    if (!name.trim() || !inputs.length) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/periods/${period.id}/scenarios`,
        { name: name.trim(), inputs });
      toast.success("Scenario creato (bozza)");
      setShowCreate(false);
      setName("");
      setRows([{ code: "", type: "PERCENT_CHANGE", value: "" }]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const calculate = async (scenario) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${period.id}/scenarios/${scenario.id}/calculate`);
      setResult({ ...data, scenario_name: scenario.name });
      toast.success("Simulazione calcolata");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const clone = async (scenario) => {
    try {
      await api.post(
        `/companies/${companyId}/periods/${period.id}/scenarios/${scenario.id}/clone`);
      toast.success("Scenario duplicato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (scenario) => {
    if (!window.confirm(`Eliminare lo scenario "${scenario.name}"?`)) return;
    try {
      await api.delete(
        `/companies/${companyId}/periods/${period.id}/scenarios/${scenario.id}`);
      toast.success("Scenario eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="scenarios-section">
      <Card className="border-slate-200" data-testid="scenario-engine-card">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-green-700" aria-hidden="true" />
              Scenari sullo Score ESG PMI (metodologia Fase 9)
            </CardTitle>
            <Button size="sm" variant="outline" className="h-8 text-xs"
              onClick={() => setShowCreate((v) => !v)} data-testid="toggle-scenario-form">
              <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Nuovo scenario
            </Button>
          </div>
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2"
            data-testid="simulation-label">
            {label || "SIMULAZIONE — NON È UN DATO REALE"} — risultato ipotetico basato sulle assunzioni dello scenario.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {showCreate && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
              data-testid="scenario-form">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Nome scenario (es. Riduzione energia 15% + Codice Etico)" maxLength={200}
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
                data-testid="scenario-name-input" aria-label="Nome scenario" />
              {rows.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`scenario-row-${i}`}>
                  <select value={r.code} onChange={(e) => setRow(i, { code: e.target.value })}
                    className="flex-1 min-w-[220px] h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
                    data-testid={`scenario-code-${i}`} aria-label="Datapoint">
                    <option value="">Seleziona dato canonico…</option>
                    {options.map((o) => (
                      <option key={o.datapoint_code} value={o.datapoint_code}>
                        {`${o.datapoint_code} — ${o.label}`}
                      </option>
                    ))}
                    <option value="GOV.ETHICS.CODE_EXISTS">GOV.ETHICS.CODE_EXISTS — Codice Etico</option>
                    <option value="GOV.231.ADOPTED">GOV.231.ADOPTED — Modello 231</option>
                    <option value="GOV.WB.CHANNEL_EXISTS">GOV.WB.CHANNEL_EXISTS — Whistleblowing</option>
                  </select>
                  <select value={r.type} onChange={(e) => setRow(i, { type: e.target.value })}
                    className="w-[150px] h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
                    data-testid={`scenario-type-${i}`} aria-label="Tipo ipotesi">
                    {Object.entries(ASSUMPTION_LABELS).map(([k, l]) => (
                      <option key={k} value={k}>{l}</option>
                    ))}
                  </select>
                  {r.type === "BOOLEAN_CHANGE" ? (
                    <select value={r.value} onChange={(e) => setRow(i, { value: e.target.value })}
                      className="w-[110px] h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
                      data-testid={`scenario-value-${i}`} aria-label="Valore">
                      <option value="">…</option>
                      <option value="true">Sì</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input type="number" value={r.value}
                      onChange={(e) => setRow(i, { value: e.target.value })}
                      placeholder={r.type === "PERCENT_CHANGE" ? "es. -15" : "valore"}
                      className="w-[110px] h-9 rounded-md border border-slate-300 bg-white px-2 text-xs"
                      data-testid={`scenario-value-${i}`} aria-label="Valore ipotesi" />
                  )}
                  {rows.length > 1 && (
                    <button type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                      className="text-red-500 hover:text-red-700" data-testid={`scenario-row-remove-${i}`}
                      aria-label="Rimuovi ipotesi">
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => setRows((rs) => [...rs, { code: "", type: "PERCENT_CHANGE", value: "" }])}
                  data-testid="scenario-add-row">
                  <Plus className="w-3 h-3 mr-1" aria-hidden="true" />Aggiungi ipotesi
                </Button>
                <Button size="sm" disabled={busy || !name.trim()}
                  onClick={create}
                  className="bg-green-700 hover:bg-green-800 text-white h-8 text-xs"
                  data-testid="scenario-create-button">
                  {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />}
                  Salva scenario
                </Button>
              </div>
            </div>
          )}

          {scenarios === null ? <p className="text-sm text-slate-500">Caricamento…</p>
            : scenarios.length === 0 && !showCreate ? (
              <p className="text-sm text-slate-600" data-testid="scenarios-empty">
                Crea uno scenario per valutare l'impatto potenziale di una o più azioni.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="scenarios-list">
                {scenarios.map((s) => (
                  <li key={s.id} className="py-3 flex flex-wrap items-center gap-3"
                    data-testid={`scenario-row-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{s.name}</p>
                      <p className="text-xs text-slate-500">
                        {s.inputs_count} ipotesi · metodologia {s.methodology_version || "—"} · {s.status}
                      </p>
                    </div>
                    {s.stale && (
                      <Badge className="bg-amber-100 text-amber-800" data-testid={`scenario-stale-${s.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" aria-hidden="true" />Base cambiata — da ricalcolare
                      </Badge>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" disabled={busy} onClick={() => calculate(s)}
                        className="bg-green-700 hover:bg-green-800 text-white h-7 text-xs"
                        data-testid={`scenario-calculate-${s.id}`}>
                        <Play className="w-3 h-3 mr-1" aria-hidden="true" />Simula
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => clone(s)} data-testid={`scenario-clone-${s.id}`}>
                        <Copy className="w-3 h-3" aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="outline"
                        className="h-7 text-xs text-red-600 border-red-200"
                        onClick={() => remove(s)} data-testid={`scenario-delete-${s.id}`}>
                        <Trash2 className="w-3 h-3" aria-hidden="true" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      </Card>

      {result && (
        <Card className="border-green-200" data-testid="scenario-result-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">
              Risultato scenario: {result.scenario_name}
              <Badge className="ml-2 bg-amber-100 text-amber-800">{result.label}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <p className="text-xs text-slate-500">Oggi</p>
                <p className="text-3xl font-extrabold text-slate-900" data-testid="scenario-current-score">
                  {result.current_score ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Scenario</p>
                <p className="text-3xl font-extrabold text-green-800" data-testid="scenario-simulated-score">
                  {result.simulated_score ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Delta</p>
                <p className="text-3xl"><DeltaValue value={result.delta_overall} testid="scenario-delta" /></p>
              </div>
              <div className="text-xs text-slate-500">Confidenza assunzioni: {result.confidence}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["E", "S", "G"].map((d) => (
                <div key={d} className="rounded-lg border border-slate-200 p-3 text-center"
                  data-testid={`scenario-dim-${d}`}>
                  <p className="text-xs text-slate-500">{DIM_LABELS[d]}</p>
                  <p className="text-sm font-bold text-slate-800">
                    {result[{ E: "environmental_current", S: "social_current", G: "governance_current" }[d]] ?? "—"}
                    {" → "}
                    {result[{ E: "environmental_simulated", S: "social_simulated", G: "governance_simulated" }[d]] ?? "—"}
                  </p>
                  <DeltaValue value={result.dimension_deltas?.[d]} testid={`scenario-dim-delta-${d}`} />
                </div>
              ))}
            </div>
            {result.metric_deltas?.length > 0 && (
              <div data-testid="scenario-metric-deltas">
                <p className="text-xs font-semibold text-slate-600 mb-1">Cosa spiega il delta</p>
                <ul className="text-xs text-slate-600 space-y-0.5">
                  {result.metric_deltas.map((m) => (
                    <li key={m.metric_code}>
                      {m.label}: {m.current_raw ?? "—"} → {m.simulated_raw ?? "—"}
                      {" · "}score {m.current_score ?? "—"} → {m.simulated_score ?? "—"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.assumption_errors?.length > 0 && (
              <p className="text-xs text-amber-700">
                {result.assumption_errors.length} ipotesi non applicabili (es. valore attuale mancante).
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
